import React from 'react';

// The (br) marker in the builder string flags an optional line break. We use
// (br) instead of an <br> tag because HTML characters get stripped from the
// banner text.
//
// When showLineBreak is false (desktop placements, or copy without a (br)) the
// marker is collapsed to a space and the copy stays on one line with any colon
// intact.
//
// When showLineBreak is true (mobile placement with a (br) present) we render
// BOTH the break and the separator colon into the DOM and let CSS pick per
// viewport — the break must only happen on phones, not tablets, even though the
// mobile placement itself renders below Bootstrap's lg breakpoint (992px):
//   - the <br> uses `d-md-none`, so it breaks below 768px (phones) and is
//     suppressed at >=768px (tablets), keeping tablets on one line
//   - the separator colon uses `d-none d-md-inline`, the inverse — hidden on
//     phones (the break replaces it) and shown at >=768px
// Incidental colons (e.g. a time like 9:00) are mid-line, not a trailing
// separator, so they are never toggled and always render.
export const formatBannerSegment = (text, showLineBreak) => {
    if (!text) return text;

    if (!showLineBreak) {
        return text.replace(/\s*\(br\)\s*/gi, ' ').trim();
    }

    const lines = text.split(/\s*\(br\)\s*/i);
    return lines.map((line, index) => {
        const isLastLine = index === lines.length - 1;
        const hasTrailingColon = /\s*:\s*$/.test(line);
        return (
            <React.Fragment key={index}>
                {line.replace(/\s*:\s*$/, '')}
                {hasTrailingColon && <span className="d-none d-md-inline">: </span>}
                {/* Space keeps the two lines separated once the <br> is hidden on
                    tablet; it collapses with the colon's space when both render. */}
                {!isLastLine && (
                    <>
                        {' '}
                        <br className="d-md-none" />
                    </>
                )}
            </React.Fragment>
        );
    });
};

// Splits the copy on the pipe into a bold lead-in (first part) and a regular
// remainder (second part). Throws on non-string input; returns undefined parts
// for nullish input so callers can render an empty banner while data loads.
export const splitByPipe = (text) => {
    if (text && typeof text !== 'string') {
        throw new Error('Input must be a string');
    }

    const parts = text?.split('|')?.map((part) => part.trim()) ?? [];
    return { firstPart: parts[0], secondPart: parts[1] };
};

// The shared banner/promo formatting: a bold first part and a regular second
// part (split on the pipe), each run through formatBannerSegment so the (br)
// line-break and trailing-colon handling apply consistently.
//
// supportsLineBreak is the placement's capability; line breaks only render when
// the placement supports two lines AND the copy actually contains a (br) marker.
//
// The type is set by .promo-banner-text in Header.css rather than by Tailwind
// utilities: tailwind.config.js only scans .ts/.tsx, so utilities named here in a
// .js file are never generated (`leading-6` never existed in the bundle; the 24px
// line height came from `text-base`, which other .tsx files happen to emit). The
// countdown header heights reserved in global.css are derived from that line
// height, so it needs to be declared somewhere it cannot silently vanish.
export const formatPromoText = (text, { supportsLineBreak = false } = {}) => {
    const { firstPart, secondPart } = splitByPipe(text);
    const showLineBreak = supportsLineBreak && /\(br\)/i.test(text ?? '');

    return (
        <div className="promo-banner-text">
            <span className="promo-banner-lead">{formatBannerSegment(firstPart, showLineBreak)}</span>{' '}
            <span>{formatBannerSegment(secondPart, showLineBreak)}</span>
        </div>
    );
};
